import type { Observable } from 'rxjs';
import { catchError, defer, finalize, map, of, switchMap, throwError } from 'rxjs';
import { shareReplayRefCount } from '../../../../../utils/rxjs';
import type { RenderRuntimeDataServices } from '../../elements/types';
import type {
    RuntimeDataServiceId,
    RuntimeDataServiceInput,
    RuntimeDataServiceOutput,
} from './contracts';
import { createActiveBindingRegistry } from './activeBindingRegistry';
import { normalizeBindingArgForPolicy } from './bindingArgPolicy';
import { createBindingId } from './bindingId';
import { getResultLinkDescriptorOrThrow } from './resultLinks';
import {
    assertRuntimeBindingCompatibilityMetadata,
    assertRuntimeBindingCompatibleWithContext,
} from './bindingCompatibility';
import type {
    RuntimeDataService,
    RuntimeDataServiceInstance,
    ActiveDataBinding,
    ActiveBindingObserverConfig,
    DebugServiceCatalogItem,
    RuntimeBindingResolveContext,
} from './types';

function normalizeError(error: unknown): Error {
    if (error instanceof Error) {
        return error;
    }

    return new Error(String(error));
}

function isObserverConfig(value: unknown): value is ActiveBindingObserverConfig {
    return (
        typeof value === 'object' &&
        value !== null &&
        'elementPath' in value &&
        typeof value.elementPath === 'string'
    );
}

function resolveGraphTimeWindowOrThrow(
    bindingId: string,
    requiresGraphTimeWindow: boolean | undefined,
    resolveContext: RuntimeBindingResolveContext
) {
    const { graphTimeWindow } = resolveContext;
    if (requiresGraphTimeWindow && !graphTimeWindow) {
        throw new Error(`Runtime binding "${bindingId}" requires graph time window.`);
    }

    return graphTimeWindow;
}

class RuntimeDataServiceRegistry {
    private readonly servicesById: Map<RuntimeDataServiceId, RuntimeDataServiceInstance>;

    private readonly inFlightRequestStreamsByKey = new Map<string, Observable<unknown>>();

    readonly activeBindingRegistry;

    constructor(services: readonly RuntimeDataServiceInstance[]) {
        this.servicesById = new Map<RuntimeDataServiceId, RuntimeDataServiceInstance>();
        services.forEach((service) => {
            if (this.servicesById.has(service.id)) {
                throw new Error(`Duplicate runtime data service id "${service.id}".`);
            }

            service.resultLinks.forEach((link) => {
                const bindingId = `${service.id}.${link.key}` as const;
                const hasBuildInput = typeof link.buildInput === 'function';
                const hasReactiveInput = typeof link.resolveBuildInput$ === 'function';
                if (
                    !(
                        (hasBuildInput && link.resolveBuildInput$ === undefined) ||
                        (hasReactiveInput && link.buildInput === undefined)
                    )
                ) {
                    throw new Error(
                        `Runtime binding "${bindingId}" must define exactly one input resolver: buildInput or resolveBuildInput$.`
                    );
                }
                assertRuntimeBindingCompatibilityMetadata(bindingId, link);
            });

            this.servicesById.set(service.id, service);
        });

        this.activeBindingRegistry = createActiveBindingRegistry(this.servicesById, {
            requestData$: this.requestData$.bind(this),
        });
    }

    private getServiceOrThrow<TServiceId extends RuntimeDataServiceId>(
        serviceId: TServiceId
    ): RuntimeDataService<TServiceId> {
        const service = this.servicesById.get(serviceId);
        if (!service) {
            throw new Error(`Unknown runtime data service "${serviceId}".`);
        }

        return service as RuntimeDataService<TServiceId>;
    }

    private requestData$<TServiceId extends RuntimeDataServiceId>(
        serviceId: TServiceId,
        input: RuntimeDataServiceInput<TServiceId>
    ): Observable<RuntimeDataServiceOutput<TServiceId>> {
        return defer(() => this.requestData$Internal(serviceId, input, []));
    }

    private requestData$Internal<TServiceId extends RuntimeDataServiceId>(
        serviceId: TServiceId,
        input: RuntimeDataServiceInput<TServiceId>,
        requestStack: readonly string[]
    ): Observable<RuntimeDataServiceOutput<TServiceId>> {
        const service = this.getServiceOrThrow(serviceId);
        const validatedInput = service.validateArgs(input);
        const requestIdentifier = this.createRequestIdentifier(serviceId, validatedInput);
        const requestKey = requestIdentifier;

        if (requestStack.includes(requestIdentifier)) {
            const cyclePath = [...requestStack, requestIdentifier].join(' -> ');
            throw new Error(`Runtime data service dependency cycle detected: ${cyclePath}.`);
        }

        const existingInFlightRequestStream = this.inFlightRequestStreamsByKey.get(requestKey);
        if (existingInFlightRequestStream) {
            return existingInFlightRequestStream as Observable<
                RuntimeDataServiceOutput<TServiceId>
            >;
        }

        const request$ = defer(() =>
            service.createResult$({
                input: validatedInput,
                deps: {
                    requestData$: (depServiceId, depInput) =>
                        this.requestData$Internal(depServiceId, depInput, [
                            ...requestStack,
                            requestIdentifier,
                        ]),
                },
            })
        ).pipe(
            map((result) => service.validateResult(result)),
            catchError((error) => throwError(() => normalizeError(error))),
            finalize(() => {
                this.inFlightRequestStreamsByKey.delete(requestKey);
            }),
            shareReplayRefCount()
        );

        this.inFlightRequestStreamsByKey.set(requestKey, request$ as Observable<unknown>);

        return request$;
    }

    observeBinding<TServiceId extends RuntimeDataServiceId>(
        binding: ActiveDataBinding<TServiceId>,
        observer: ActiveBindingObserverConfig,
        resolveContext: RuntimeBindingResolveContext = {}
    ): Observable<unknown> {
        if (!isObserverConfig(observer)) {
            throw new Error('Expected runtime data binding observer configuration.');
        }

        const { binding: normalizedBinding, input$ } = this.resolveBindingInput(
            binding,
            resolveContext
        );
        return input$.pipe(
            switchMap((input) =>
                this.activeBindingRegistry.observeBinding(normalizedBinding, input, observer)
            )
        );
    }

    listCatalog(): readonly DebugServiceCatalogItem[] {
        return Array.from(this.servicesById.values()).map((service) => ({
            serviceId: service.id,
            label: service.metadata.label,
            description: service.metadata.description,
            // We cannot pass `service.resultLinks` directly: link descriptors include runtime
            // builders (for example `buildInput`) that are intentionally excluded
            // from the debug DTO.
            links: service.resultLinks.map((link) => ({
                key: link.key,
                label: link.label,
                description: link.description,
                path: link.path,
                valueKind: link.valueKind,
                supportedContextKinds: link.supportedContextKinds,
                supportedSwarmTypes: link.supportedSwarmTypes,
            })),
        }));
    }

    private createRequestIdentifier<TServiceId extends RuntimeDataServiceId>(
        serviceId: TServiceId,
        input: RuntimeDataServiceInput<TServiceId>
    ): string {
        return `${serviceId}:${JSON.stringify(input)}`;
    }

    private resolveBindingInput<TServiceId extends RuntimeDataServiceId>(
        binding: ActiveDataBinding<TServiceId>,
        resolveContext: RuntimeBindingResolveContext
    ): {
        binding: ActiveDataBinding<TServiceId>;
        input$: Observable<RuntimeDataServiceInput<TServiceId>>;
    } {
        const link = getResultLinkDescriptorOrThrow(this.servicesById, binding);
        const bindingId = createBindingId(binding);
        if (link.supportedSwarmTypes !== null) {
            const { context } = resolveContext;
            if (!context) {
                throw new Error(
                    `Runtime binding "${bindingId}" requires a measuring-point context for SWARM compatibility.`
                );
            }
            assertRuntimeBindingCompatibleWithContext(bindingId, link, context);
        }
        const normalizedArg = normalizeBindingArgForPolicy(
            binding.arg,
            link.argPolicy,
            bindingId,
            link.argOptions
        );
        const normalizedBinding: ActiveDataBinding<TServiceId> = {
            serviceId: binding.serviceId,
            linkKey: binding.linkKey,
            ...(normalizedArg === null ? {} : { arg: normalizedArg }),
        };
        const graphTimeWindow = resolveGraphTimeWindowOrThrow(
            bindingId,
            link.requiresGraphTimeWindow,
            resolveContext
        );

        const args = {
            context: resolveContext.context,
            graphTimeWindow,
            graphSettings: resolveContext.graphSettings,
            arg: normalizedArg,
            templateId:
                typeof resolveContext.templateId === 'undefined' ? null : resolveContext.templateId,
        };
        const { resolveBuildInput$ } = link;

        return {
            binding: normalizedBinding,
            input$: resolveBuildInput$
                ? defer(() =>
                      resolveBuildInput$({
                          ...args,
                          getDataSetContext: () => {
                              if (!resolveContext.getDataSetContext) {
                                  throw new Error(
                                      `Runtime binding "${bindingId}" requires a DataSet context getter.`
                                  );
                              }
                              return resolveContext.getDataSetContext();
                          },
                      })
                  )
                : of(link.buildInput(args)),
        };
    }
}

export function createRuntimeDataServiceRegistry(
    services: readonly RuntimeDataServiceInstance[]
): RenderRuntimeDataServices {
    const registry = new RuntimeDataServiceRegistry(services);
    const debugRegistry = registry.activeBindingRegistry;

    return {
        observeBinding: registry.observeBinding.bind(registry),
        debug: {
            listCatalog: registry.listCatalog.bind(registry),
            observeActiveBindingInstanceKeys:
                debugRegistry.observeActiveBindingInstanceKeys.bind(debugRegistry),
            observeActiveBindingDebug: debugRegistry.observeActiveBindingDebug.bind(debugRegistry),
            observeActiveBindingInput: debugRegistry.observeActiveBindingInput.bind(debugRegistry),
        },
    };
}
